import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Loader2, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatPrice } from "@/lib/api";
import { STATUS_LABELS, STATUS_COLORS } from "@/lib/orderStatus";

export default function Account() {
  const { user, updateProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "", county: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", address: user.address || "", city: user.city || "", county: user.county || "" });
  }, [user]);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Date salvate");
    } catch {
      toast.error("Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8" data-testid="account-page">
      <h1 className="mb-8 font-heading text-3xl font-bold tracking-tight text-slate-900">Contul meu</h1>

      <Tabs defaultValue="orders">
        <TabsList className="mb-6">
          <TabsTrigger value="orders" data-testid="tab-orders"><Package className="mr-2 h-4 w-4" /> Comenzile mele</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><UserIcon className="mr-2 h-4 w-4" /> Date personale</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : orders.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center" data-testid="no-orders">
              <Package className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-4 text-slate-500">Nu ai plasat nicio comandă încă.</p>
              <Button asChild className="mt-6 rounded-full"><Link to="/magazin">Mergi la magazin</Link></Button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="rounded-xl border border-slate-200 p-5" data-testid={`order-${o.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-mono text-sm font-semibold text-slate-900">#{o.order_number}</p>
                      <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    <Badge className={STATUS_COLORS[o.status]}>{STATUS_LABELS[o.status] || o.status}</Badge>
                  </div>
                  <div className="mt-3 text-sm text-slate-600">{o.items.length} produse · <span className="font-semibold text-slate-900">{formatPrice(o.total)}</span></div>
                  <Link to={`/comanda/${o.id}`} className="mt-2 inline-block text-sm font-medium text-primary hover:underline">Vezi detalii</Link>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile">
          <form onSubmit={save} className="max-w-lg space-y-4 rounded-xl border border-slate-200 p-6">
            <div>
              <Label>Email</Label>
              <Input value={user?.email || ""} disabled className="mt-1.5 bg-slate-50" />
            </div>
            <div>
              <Label htmlFor="pname">Nume complet</Label>
              <Input id="pname" value={form.name} onChange={set("name")} className="mt-1.5" data-testid="profile-name" />
            </div>
            <div>
              <Label htmlFor="pphone">Telefon</Label>
              <Input id="pphone" value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="profile-phone" />
            </div>
            <div>
              <Label htmlFor="paddr">Adresă</Label>
              <Input id="paddr" value={form.address} onChange={set("address")} className="mt-1.5" data-testid="profile-address" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pcity">Oraș</Label>
                <Input id="pcity" value={form.city} onChange={set("city")} className="mt-1.5" data-testid="profile-city" />
              </div>
              <div>
                <Label htmlFor="pcounty">Județ</Label>
                <Input id="pcounty" value={form.county} onChange={set("county")} className="mt-1.5" data-testid="profile-county" />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="rounded-full hover:bg-primary/90" data-testid="save-profile">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvează"}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
